const NUMBER_WITH_COMMAS = new Intl.NumberFormat("en-US");

/**
 * Format a number for display with thousand separators (e.g. 12345 → "12,345").
 * Returns "" for null/undefined/NaN. Use for counts, accession numbers, pages, etc.
 */
export function formatNumberWithCommas(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "";
  return NUMBER_WITH_COMMAS.format(value);
}

/**
 * Parse a user-entered number that may include commas (e.g. "12,345" → 12345).
 */
export function parseFormattedNumber(value: string): number | undefined {
  const cleaned = value.replace(/,/g, "").trim();
  if (!cleaned) return undefined;
  const num = Number(cleaned);
  return Number.isFinite(num) ? num : undefined;
}

/** Naira amount with thousands separators and two decimal places: 1500 → "₦1,500.00". */
export function formatNaira(value: number | string | null | undefined): string {
  const raw = typeof value === "string" ? value.replace(/,/g, "") : value;
  const num = typeof raw === "string" ? Number(raw) : (raw ?? 0);
  const safe = Number.isFinite(num) ? num : 0;
  const negative = safe < 0;
  const [whole, fraction] = Math.abs(safe).toFixed(2).split(".");
  const withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `${negative ? "-" : ""}₦${withCommas}.${fraction}`;
}

/** Keep a money draft displayable while typing: "12500.5" → "12,500.5". */
export function formatMoneyInput(value: string): string {
  const cleaned = value.replace(/,/g, "").replace(/[^\d.]/g, "");
  if (!cleaned) return "";
  const dot = cleaned.indexOf(".");
  const whole = dot === -1 ? cleaned : cleaned.slice(0, dot);
  const fraction =
    dot === -1
      ? ""
      : cleaned
          .slice(dot + 1)
          .replace(/\./g, "")
          .slice(0, 2);
  const withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return dot === -1 ? withCommas : `${withCommas}.${fraction}`;
}

export function generateSlug(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, "")
    .replace(/[\s_-]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function formatPhone(phone: string): string {
  // Remove all non-digit characters
  const cleaned = phone.replace(/\D/g, "");

  // Format as (XXX) XXX-XXXX for US numbers
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }

  // Return cleaned number if not 10 digits
  return cleaned;
}

export function capitalizeWords(text: string): string {
  return text
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}
